import React, { useState } from "react";
import {
  Button,
  Grid,
  TextField,
  Typography,
  Box,
  Card,
  Divider,
  CardContent,
  Stack,
  FormControlLabel,
  Switch,
  Container,
  Autocomplete,
  FormGroup,
  Checkbox,
} from "@mui/material";
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { ErrorMessage, Formik, yupToFormErrors } from "formik";
import clsx from "clsx";
import { toast } from "react-toastify";
import useBusiness from "../../../hooks/store/business";
import { useStyles } from "./styles";
import {
  getDashboardErrorMessage,
  unwrapDashboardResponse,
} from "../../../api/dashboardResponse";
import {
  getBusinessForBusiness,
  getPhorestIntegration,
  getMessengerIntegration,
  updatePhorestIntegration,
  updateMessengerIntegration,
} from "../../../api/business";
import useClients from "../../../hooks/store/clients";
import usePermissions from "../../../hooks/usePermissions";
import Loader from "../../../common/Loader";
import APIConfigSection from "./APIConfigSection";
import LegacyAssistantSection from "./LegacyAssistantSection";
import LinkButtonEditor from "./LinkButtonEditor";
import SocialTokenFields from "./SocialTokenFields";
import WidgetScriptSection from "./WidgetScriptSection";
import {
  BusinessFormSchema,
  limitedEditorSchema as createLimitedEditorSchema,
} from "./validation";
import { normalizeThemeOpenButtonBottomCss } from "./themeSettings";
import {
  addChangedLargeBusinessFields,
  addChangedStandardAiFunctionsField,
  nullIfEmptyBusinessField,
  serializeStandardAiFunctionsForCreate,
} from "./businessUpdatePayload";
import {
  buildPhorestIntegrationPayload,
  getBusinessFormFingerprint,
  getPhorestDraftValues,
  getPhorestFormValues,
  hasPhorestIntegrationChanges,
} from "./phorestIntegrationForm";
import {
  buildMessengerIntegrationPayload,
  getMessengerDraftValues,
  getMessengerFormValues,
  hasMessengerIntegrationChanges,
} from "./messengerIntegrationForm";


const initValues = {
  name: "",
  idClient: "",
  websiteUrl: "",
  address: "",
  phone: "",
  status: false,
  accessToken: "",
  clientToken:"",
  mewsClientId: "",
  apiType: "mews",
  avvioUsername: "",
  avvioPassword: "",
  hotelEmail: "",
  assistantId:"",
  greetingMessage:"",
  hotelRedirectUrl:"",
  open_by_default: false,
  openButtonStyle: "none",
  instagramDM:false,
  instagramAccessToken:'',
  whatsAppDm:false,
  whatsAppAccessToken:'',
  integrationsMessengerEnabled: false,
  integrationsMessengerPageAccessToken: '',
  integrationsMessengerPageAccessTokenConfigured: false,
  integrationsPhorestEnabled: false,
  integrationsPhorestRegion: 'EU',
  integrationsPhorestBusinessId: '',
  integrationsPhorestBranchId: '',
  integrationsPhorestCurrency: 'EUR',
  integrationsPhorestApiUsername: '',
  integrationsPhorestApiPassword: '',
  integrationsPhorestApiPasswordConfigured: false,
  rivet: false,
  systemPrompt: '',
  functionsJson: '',
  rivetYaml: '',
  themeOn: false,
  themeLogo: '',
  themePrimaryColor: '#8599c7',
  themeFont: 'Inter',
  themeOpenButtonBottomCss: '0',
  standardAiFunctions: [],
  greeting_buttons_enabled: false,
  greeting_buttons: [
    {
      title: "",
      url: "",
      open_in_popup: false,
    },
  ],
  link_buttons_enabled: false,
  link_buttons: [
    {
      title: "",
      url: "",
      open_in_popup: false,
      comment_for_ai: "",
    },
  ],
};

let widgetButtonFormKeyCounter = 0;

const createWidgetButtonFormKey = () => {
  widgetButtonFormKeyCounter += 1;
  return `widget-button-${Date.now()}-${widgetButtonFormKeyCounter}`;
};

const hasOwnDraftValue = (draftValues, fieldName) =>
  Object.prototype.hasOwnProperty.call(draftValues, fieldName);

const normalizeButton = (item = {}, { includeCommentForAi = true } = {}) => {
  const normalizedButton = {
    _formKey:
      typeof item._formKey === "string" && item._formKey.trim() !== ""
        ? item._formKey
        : createWidgetButtonFormKey(),
    title: typeof item.title === "string" ? item.title : "",
    url: typeof item.url === "string" ? item.url : "",
    open_in_popup:
      item.open_in_popup === true ||
      item.open_in_popup === 1 ||
      item.open_in_popup === "1" ||
      item.open_in_popup === "true",
  };

  if (includeCommentForAi) {
    normalizedButton.comment_for_ai =
      typeof item.comment_for_ai === "string" ? item.comment_for_ai : "";
  }

  return normalizedButton;
};

const normalizeLinkButton = (item = {}) =>
  normalizeButton(item, { includeCommentForAi: true });

const normalizeGreetingButton = (item = {}) =>
  normalizeButton(item, { includeCommentForAi: false });

const parseButtons = (value, normalizeItem) => {
  const fallback = [normalizeItem()];
  if (!value) {
    return fallback;
  }

  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return fallback;
    }

    const normalized = parsed.map((item) => normalizeItem(item));
    return normalized.length > 0 ? normalized : fallback;
  } catch (e) {
    return fallback;
  }
};

const parseLinkButtons = (value) => parseButtons(value, normalizeLinkButton);

const parseGreetingButtons = (value) => parseButtons(value, normalizeGreetingButton);

const isTruthyFlag = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  value === "true" ||
  value === "yes";

const hasInvalidButtons = (
  formValues,
  { buttonsField, enabledField, ignoreRivet = false }
) => {
  if (
    !formValues ||
    (!ignoreRivet && isTruthyFlag(formValues.rivet)) ||
    !isTruthyFlag(formValues[enabledField])
  ) {
    return false;
  }

  const buttons = Array.isArray(formValues[buttonsField]) ? formValues[buttonsField] : [];
  if (buttons.length === 0) {
    return true;
  }

  return buttons.some(
    (button) =>
      typeof button?.title !== "string" ||
      button.title.trim() === "" ||
      typeof button?.url !== "string" ||
      button.url.trim() === ""
  );
};

const hasInvalidLinkButtons = (formValues) =>
  hasInvalidButtons(formValues, {
    buttonsField: "link_buttons",
    enabledField: "link_buttons_enabled",
  });

const hasInvalidGreetingButtons = (formValues) =>
  hasInvalidButtons(formValues, {
    buttonsField: "greeting_buttons",
    enabledField: "greeting_buttons_enabled",
    ignoreRivet: true,
  });

const normalizeButtonsForPayload = (
  buttonsSource,
  { includeCommentForAi = true, createFallbackButton }
) =>
  (Array.isArray(buttonsSource) && buttonsSource.length > 0
    ? buttonsSource
    : [createFallbackButton()]
  ).map((button) => {
    const normalizedButton = {
      title: typeof button?.title === "string" ? button.title.trim() : "",
      url: typeof button?.url === "string" ? button.url.trim() : "",
      open_in_popup: !!button?.open_in_popup,
    };

    if (includeCommentForAi) {
      normalizedButton.comment_for_ai =
        typeof button?.comment_for_ai === "string"
          ? button.comment_for_ai.trim()
          : "";
    }

    return normalizedButton;
  });

const whiteFieldSx = {
  '& .MuiInputBase-root': {
    backgroundColor: '#fff !important',
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#fff !important',
  },
  '& .MuiFilledInput-root': {
    backgroundColor: '#fff !important',
  },
};

const whiteInputProps = {
  sx: { backgroundColor: '#fff' },
};

function Index() {
  const formikRef = React.useRef();
  const systemPromptInputRef = React.useRef(null);
  const systemPromptScrollTopRef = React.useRef(0);
  const functionsJsonInputRef = React.useRef(null);
  const rivetYamlInputRef = React.useRef(null);
  const largeTextDraftValuesRef = React.useRef({});
  const greetingButtonEditorRef = React.useRef(null);
  const linkButtonEditorRef = React.useRef(null);
  const fetchedBusinessIdRef = React.useRef(null);
  const businessRevisionRef = React.useRef(null);
  const businessDetailsRequestIdRef = React.useRef(0);
  const phorestRequestIdRef = React.useRef(0);
  const messengerRequestIdRef = React.useRef(0);
  const scriptVariantsRequestIdRef = React.useRef(0);
  const businessFormBaselineRef = React.useRef(null);
  const businessBaselineFormSeedRef = React.useRef(null);
  const pendingPhorestDraftRef = React.useRef(null);
  const pendingMessengerDraftRef = React.useRef(null);
  const appliedIntegrationFormsRef = React.useRef({});
  const requestedClientDataRef = React.useRef(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const businessIdFromUrl = searchParams.get("id");
  const editorGenerationRef = React.useRef(0);
  React.useLayoutEffect(() => () => {
    editorGenerationRef.current += 1;
  }, [businessIdFromUrl]);
  const classes = useStyles();
  const {
    addBusinessAction,
    updateBusinessAction,
    webScriptV2Action,
    loading,
  } = useBusiness();
  const {
    accessContextReady,
    canAccessBusiness,
    capabilities,
    clientId: scopedClientId,
    hasClientScope,
    isClient,
    isScopedUser,
  } = usePermissions();
  const { clientId, getClientIdDataAction } = useClients();
  const colorSet = useSelector((state) => state.color);
  const [edit, setEdit] = React.useState(false);
  const [copiedTarget, setCopiedTarget] = useState("");
  const [fetchedBusiness, setFetchedBusiness] = React.useState(null);
  const [businessSaveNeedsReload, setBusinessSaveNeedsReload] = React.useState(false);
  const [businessRecordRevision, setBusinessRecordRevision] = React.useState(0);
  const [fetchingBusiness, setFetchingBusiness] = React.useState(false);
  const [phorestIntegration, setPhorestIntegration] = React.useState(null);
  const [phorestLoading, setPhorestLoading] = React.useState(false);
  const [phorestLoadError, setPhorestLoadError] = React.useState(false);
  const [messengerIntegration, setMessengerIntegration] = React.useState(null);
  const [messengerLoading, setMessengerLoading] = React.useState(false);
  const [messengerLoadError, setMessengerLoadError] = React.useState(false);
  const [largeTextDraftEdits, setLargeTextDraftEdits] = React.useState({});
  const [currentScriptVariants, setCurrentScriptVariants] =
    React.useState(null);
  const isScopedBusinessUser = isScopedUser;
  const isLimitedBusinessEditor = isScopedBusinessUser && !!businessIdFromUrl;

  const limitedEditorValidationSchema = React.useMemo(
    () => createLimitedEditorSchema(),
    []
  );

  const hasPendingPhorestChanges = React.useCallback(
    (values) =>
      !isLimitedBusinessEditor &&
      edit &&
      Boolean(fetchedBusiness?.id) &&
      Boolean(phorestIntegration) &&
      !phorestLoadError &&
      hasPhorestIntegrationChanges(values, phorestIntegration),
    [
      edit,
      fetchedBusiness?.id,
      isLimitedBusinessEditor,
      phorestIntegration,
      phorestLoadError,
    ]
  );

  const hasPendingMessengerChanges = React.useCallback(
    (values) =>
      !isLimitedBusinessEditor &&
      edit &&
      Boolean(fetchedBusiness?.id) &&
      Boolean(messengerIntegration) &&
      !messengerLoadError &&
      hasMessengerIntegrationChanges(values, messengerIntegration),
    [
      edit,
      fetchedBusiness?.id,
      isLimitedBusinessEditor,
      messengerIntegration,
      messengerLoadError,
    ]
  );

  const validateBusinessForm = React.useCallback(
    async (values) => {
      const schema = isLimitedBusinessEditor
        ? limitedEditorValidationSchema
        : BusinessFormSchema({
            hasClientScope,
            isClient,
            includePhorestIntegration: hasPendingPhorestChanges(values),
            includeMessengerIntegration: hasPendingMessengerChanges(values),
          });

      try {
        await schema.validate(values, { abortEarly: false });
        return {};
      } catch (error) {
        if (error?.name !== "ValidationError") {
          throw error;
        }
        return yupToFormErrors(error);
      }
    },
    [
      hasClientScope,
      hasPendingPhorestChanges,
      hasPendingMessengerChanges,
      isClient,
      isLimitedBusinessEditor,
      limitedEditorValidationSchema,
    ]
  );

  const clearCurrentScriptVariants = React.useCallback(() => {
    scriptVariantsRequestIdRef.current += 1;
    setCurrentScriptVariants(null);
  }, []);

  const resetPhorestIntegration = React.useCallback(() => {
    phorestRequestIdRef.current += 1;
    setPhorestIntegration(null);
    setPhorestLoading(false);
    setPhorestLoadError(false);
  }, []);

  const resetMessengerIntegration = React.useCallback(() => {
    messengerRequestIdRef.current += 1;
    setMessengerIntegration(null);
    setMessengerLoading(false);
    setMessengerLoadError(false);
  }, []);

  const setLargeTextDraftValue = React.useCallback(
    (fieldName, value, savedValue) => {
      largeTextDraftValuesRef.current[fieldName] = value;
      const hasDraftEdit = value !== savedValue;

      setLargeTextDraftEdits((current) => {
        const currentValue = Boolean(current[fieldName]);
        if (currentValue === hasDraftEdit) {
          return current;
        }

        const next = { ...current };
        if (hasDraftEdit) {
          next[fieldName] = true;
        } else {
          delete next[fieldName];
        }
        return next;
      });
    },
    []
  );

  const clearLargeTextDraftEdits = React.useCallback(() => {
    largeTextDraftValuesRef.current = {};
    setLargeTextDraftEdits({});
  }, []);

  const getLargeTextDraftValue = React.useCallback(
    (fieldName, inputRef, fallbackValue) => {
      if (inputRef.current) {
        return inputRef.current.value;
      }

      if (hasOwnDraftValue(largeTextDraftValuesRef.current, fieldName)) {
        return largeTextDraftValuesRef.current[fieldName];
      }

      return fallbackValue;
    },
    []
  );

  const resetLoadedBusinessDetails = React.useCallback(() => {
    businessDetailsRequestIdRef.current += 1;
    fetchedBusinessIdRef.current = null;
    businessRevisionRef.current = null;
    setBusinessSaveNeedsReload(false);
    setFetchedBusiness(null);
    setBusinessRecordRevision((revision) => revision + 1);
    setEdit(false);
    setFetchingBusiness(false);
    businessFormBaselineRef.current = null;
    businessBaselineFormSeedRef.current = null;
    pendingPhorestDraftRef.current = null;
    pendingMessengerDraftRef.current = null;
    resetPhorestIntegration();
    resetMessengerIntegration();
    clearLargeTextDraftEdits();
    clearCurrentScriptVariants();
  }, [
    clearCurrentScriptVariants,
    clearLargeTextDraftEdits,
    resetMessengerIntegration,
    resetPhorestIntegration,
  ]);

  const loadBusinessDetails = React.useCallback(
    async (
      businessId,
      {
        redirectOnMissing = false,
        redirectOnError = false,
        fallbackMessage = "Failed to load business details",
      } = {}
    ) => {
      const requestId = businessDetailsRequestIdRef.current + 1;
      const generation = editorGenerationRef.current;
      businessDetailsRequestIdRef.current = requestId;
      const isCurrentRequest = () =>
        generation === editorGenerationRef.current && requestId === businessDetailsRequestIdRef.current;

      setFetchingBusiness(true);

      try {
        const res = await getBusinessForBusiness({ businessId });
        if (!isCurrentRequest()) {
          return null;
        }

        const body = unwrapDashboardResponse(res, fallbackMessage);
        const loadedBusiness = body?.businessList?.[0];

        if (!loadedBusiness) {
          toast.error("Business not found");
          if (redirectOnMissing) {
            navigate("/business", { replace: true });
          }
          return null;
        }

        businessRevisionRef.current = loadedBusiness.revision;
        setBusinessSaveNeedsReload(false);
        setFetchedBusiness(loadedBusiness);
        setBusinessRecordRevision((revision) => revision + 1);
        clearLargeTextDraftEdits();
        setEdit(true);

        return loadedBusiness;
      } catch (error) {
        if (!isCurrentRequest()) {
          return null;
        }

        toast.error(getDashboardErrorMessage(error, fallbackMessage));
        if (redirectOnError) {
          navigate("/business", { replace: true });
        }
        return null;
      } finally {
        if (isCurrentRequest()) {
          setFetchingBusiness(false);
        }
      }
    },
    [clearLargeTextDraftEdits, navigate]
  );

  const loadPhorestIntegrationDetails = React.useCallback(async (businessId, savedIntegration = null) => {
    const requestId = phorestRequestIdRef.current + 1;
    const generation = editorGenerationRef.current;
    phorestRequestIdRef.current = requestId;
    const isCurrentRequest = () => generation === editorGenerationRef.current && requestId === phorestRequestIdRef.current;

    setPhorestLoading(true);
    setPhorestLoadError(false);

    try {
      const response = await getPhorestIntegration(businessId);
      if (!isCurrentRequest()) {
        return null;
      }

      const integration = unwrapDashboardResponse(
        response,
        "Failed to load Phorest settings"
      );
      setPhorestIntegration(integration);
      return integration;
    } catch (error) {
      if (!isCurrentRequest()) {
        return null;
      }

      setPhorestIntegration(savedIntegration);
      setPhorestLoadError(!savedIntegration);
      toast.error(
        getDashboardErrorMessage(error, "Business loaded, but Phorest settings are unavailable")
      );
      return null;
    } finally {
      if (isCurrentRequest()) {
        setPhorestLoading(false);
      }
    }
  }, []);

  const loadMessengerIntegrationDetails = React.useCallback(async (businessId, savedIntegration = null) => {
    const requestId = messengerRequestIdRef.current + 1;
    const generation = editorGenerationRef.current;
    messengerRequestIdRef.current = requestId;
    const isCurrentRequest = () => generation === editorGenerationRef.current && requestId === messengerRequestIdRef.current;

    setMessengerLoading(true);
    setMessengerLoadError(false);

    try {
      const response = await getMessengerIntegration(businessId);
      if (!isCurrentRequest()) {
        return null;
      }

      const integration = unwrapDashboardResponse(
        response,
        "Failed to load Facebook Messenger settings"
      );
      setMessengerIntegration(integration);
      return integration;
    } catch (error) {
      if (!isCurrentRequest()) {
        return null;
      }

      setMessengerIntegration(savedIntegration);
      setMessengerLoadError(!savedIntegration);
      toast.error(
        getDashboardErrorMessage(
          error,
          "Business loaded, but Facebook Messenger settings are unavailable"
        )
      );
      return null;
    } finally {
      if (isCurrentRequest()) {
        setMessengerLoading(false);
      }
    }
  }, []);

  React.useEffect(() => {
    return () => {
      businessDetailsRequestIdRef.current += 1;
      phorestRequestIdRef.current += 1;
      messengerRequestIdRef.current += 1;
      scriptVariantsRequestIdRef.current += 1;
    };
  }, []);

  React.useEffect(() => {
    if (isScopedBusinessUser && !businessIdFromUrl) {
      navigate("/business", { replace: true });
    }
  }, [businessIdFromUrl, isScopedBusinessUser, navigate]);

  // Always fetch business by ID from URL - single source of truth
  React.useEffect(() => {
    if (!businessIdFromUrl) {
      if (fetchedBusinessIdRef.current) {
        resetLoadedBusinessDetails();
      }
      return;
    }

    if (isScopedBusinessUser) {
      if (!accessContextReady) {
        return;
      }

      if (!canAccessBusiness(businessIdFromUrl)) {
        resetLoadedBusinessDetails();
        navigate("/business", { replace: true });
        return;
      }
    }

    if (fetchedBusinessIdRef.current !== businessIdFromUrl) {
      fetchedBusinessIdRef.current = businessIdFromUrl;
      businessRevisionRef.current = null;
      setBusinessSaveNeedsReload(false);
      setFetchedBusiness(null);
      setEdit(false);
      businessFormBaselineRef.current = null;
      businessBaselineFormSeedRef.current = null;
      pendingPhorestDraftRef.current = null;
      pendingMessengerDraftRef.current = null;
      resetPhorestIntegration();
      resetMessengerIntegration();
      clearLargeTextDraftEdits();
      clearCurrentScriptVariants();
      loadBusinessDetails(businessIdFromUrl, {
        redirectOnMissing: true,
        redirectOnError: true,
      });
    }
  }, [
    accessContextReady,
    businessIdFromUrl,
    canAccessBusiness,
    clearCurrentScriptVariants,
    clearLargeTextDraftEdits,
    isScopedBusinessUser,
    loadBusinessDetails,
    navigate,
    resetMessengerIntegration,
    resetPhorestIntegration,
    resetLoadedBusinessDetails,
  ]);

  // Single source: always use fetched business data
  const business = fetchedBusiness;
  const formSeed =
    edit && business?.id ? `${business.id}-${businessRecordRevision}` : "new";

  const title = edit || isLimitedBusinessEditor ? "Edit" : "Add New";
  const hasUnsavedLargeTextEdits = React.useMemo(
    () => Object.values(largeTextDraftEdits).some(Boolean),
    [largeTextDraftEdits]
  );

  React.useEffect(() => {
    if (!edit || !business?.id || !capabilities?.canCreateBusinesses) {
      resetPhorestIntegration();
      resetMessengerIntegration();
      return;
    }

    loadPhorestIntegrationDetails(business.id);
    loadMessengerIntegrationDetails(business.id);
  }, [
    business?.id,
    capabilities?.canCreateBusinesses,
    edit,
    loadMessengerIntegrationDetails,
    resetMessengerIntegration,
    loadPhorestIntegrationDetails,
    resetPhorestIntegration,
  ]);

  React.useEffect(() => {
    if (!business?.id || !capabilities?.canCreateBusinesses) {
      clearCurrentScriptVariants();
      return undefined;
    }

    const requestId = scriptVariantsRequestIdRef.current + 1;
    scriptVariantsRequestIdRef.current = requestId;
    setCurrentScriptVariants(null);

    webScriptV2Action(business.id).then((action) => {
      if (requestId !== scriptVariantsRequestIdRef.current) {
        return;
      }

      if (action.meta?.requestStatus === "fulfilled") {
        setCurrentScriptVariants(action.payload || null);
      } else {
        setCurrentScriptVariants(null);
      }
    });

    return () => {
      if (requestId === scriptVariantsRequestIdRef.current) {
        scriptVariantsRequestIdRef.current += 1;
      }
    };
  }, [
    business?.id,
    businessRecordRevision,
    capabilities?.canCreateBusinesses,
    clearCurrentScriptVariants,
    webScriptV2Action,
  ]);

  const clientById = React.useMemo(() => {
    if (!Array.isArray(clientId)) {
      return {};
    }

    return clientId.reduce((acc, option) => {
      if (option?.id) {
        acc[option.id] = option;
      }
      return acc;
    }, {});
  }, [clientId]);

  const initialFormValues = React.useMemo(() => {
    if (edit && business) {
      let standardAiFunctions = [];
      if (business.standard_ai_functions) {
        try {
          const parsed = JSON.parse(business.standard_ai_functions);
          if (Array.isArray(parsed)) {
            standardAiFunctions = parsed.filter(
              (item) => typeof item === "string" && item !== "show_url_popup"
            );
          }
        } catch (e) {}
      }

      return {
        ...initValues,
        idClient: business?.idClient || "",
        name: business?.name || "",
        address: business?.address || "",
        websiteUrl: business?.websiteUrl || "",
        phone: business?.phone || "",
        status: business?.status === "Active" || false,
        accessToken: business?.accessToken || "",
        clientToken: business?.clientToken || "",
        mewsClientId: business?.mewsClientId || "",
        apiType: business?.apiType || "mews",
        avvioUsername: business?.avvioUsername || "",
        avvioPassword: business?.avvioPassword || "",
        hotelEmail: business?.hotelEmail || "",
        assistantId: business?.assistantId || "",
        greetingMessage: business?.greetingMessage || "",
        hotelRedirectUrl: business?.hotelRedirectUrl || "",
        open_by_default:
          business?.open_by_default === 1 ||
          business?.open_by_default === "1" ||
          business?.open_by_default === true ||
          business?.open_by_default === "true",
        openButtonStyle:
          business?.openButtonStyle === "new" ? "new" : initValues.openButtonStyle,
        instagramDM: business?.instagramDm === "yes",
        instagramAccessToken: business?.instagramAccessToken || "",
        whatsAppDm: business?.whatsAppDm === "yes",
        whatsAppAccessToken: business?.whatsAppAccessToken || "",
        rivet: business?.rivet === 1 || business?.rivet === "1" || false,
        systemPrompt: business?.system_prompt || "",
        functionsJson: business?.functions_json || "",
        rivetYaml: business?.rivet_yaml || "",
        greeting_buttons_enabled:
          business?.greeting_buttons_enabled === 1 ||
          business?.greeting_buttons_enabled === "1" ||
          business?.greeting_buttons_enabled === true ||
          business?.greeting_buttons_enabled === "true",
        greeting_buttons: parseGreetingButtons(business?.greeting_buttons),
        themeOn: business?.themeOn === 1 || business?.themeOn === "1" || false,
        themeLogo: business?.themeLogo || "",
        themePrimaryColor: business?.themePrimaryColor || initValues.themePrimaryColor,
        themeFont: business?.themeFont || initValues.themeFont,
        themeOpenButtonBottomCss:
          business?.themeOpenButtonBottomCss == null
            ? '0'
            : String(business.themeOpenButtonBottomCss),
        standardAiFunctions,
        link_buttons_enabled:
          business?.link_buttons_enabled === 1 ||
          business?.link_buttons_enabled === "1" ||
          business?.link_buttons_enabled === true ||
          business?.link_buttons_enabled === "true",
        link_buttons: parseLinkButtons(business?.link_buttons),
      };
    }
    return {
      ...initValues,
      greeting_buttons: parseGreetingButtons(initValues.greeting_buttons),
      link_buttons: parseLinkButtons(initValues.link_buttons),
    };
  }, [edit, business]);

  if (businessBaselineFormSeedRef.current !== formSeed) {
    businessBaselineFormSeedRef.current = formSeed;
    businessFormBaselineRef.current = getBusinessFormFingerprint(initialFormValues);
  }

  React.useEffect(() => {
    if (!edit || !business?.id || !formikRef.current) {
      return;
    }
    const previous = appliedIntegrationFormsRef.current;
    const newForm = previous.formSeed !== formSeed;
    const patch = {};
    if (phorestIntegration && (newForm || previous.phorest !== phorestIntegration)) {
      Object.assign(patch, getPhorestFormValues(phorestIntegration));
    }
    if (messengerIntegration && (newForm || previous.messenger !== messengerIntegration)) {
      Object.assign(patch, getMessengerFormValues(messengerIntegration));
    }
    if (newForm) {
      Object.assign(patch, pendingPhorestDraftRef.current, pendingMessengerDraftRef.current);
      pendingPhorestDraftRef.current = null;
      pendingMessengerDraftRef.current = null;
    }
    appliedIntegrationFormsRef.current = { formSeed, phorest: phorestIntegration, messenger: messengerIntegration };
    // Merge once: separate setValues calls can overwrite each other's provider fields.
    if (Object.keys(patch).length > 0) {
      formikRef.current.setValues({ ...formikRef.current.values, ...patch }, false);
    }
  }, [business?.id, formSeed, edit, phorestIntegration, messengerIntegration]);

  React.useEffect(() => {
    if (capabilities?.canManageClients && !requestedClientDataRef.current) {
      requestedClientDataRef.current = true;
      getClientIdDataAction();
    }
   
  }, [capabilities, getClientIdDataAction]);

  const copyToClipboard = (text, key) => {
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          setCopiedTarget(key);
        })
        .catch((error) => {
          setCopiedTarget("");
          console.error("Failed to copy text:", error);
        });
    } else {
      const element = document.createElement("textarea");
      element.value = text;
      document.body.appendChild(element);
      element.select();
      document.execCommand("copy");
      document.body.removeChild(element);
      setCopiedTarget(key);
    }
  };

  const savedBusinessScriptVariants =
    edit && business?.id ? currentScriptVariants : null;
  const scriptSections = [
    {
      key: "prod",
      title: "Prod script",
      value: savedBusinessScriptVariants?.prod || "",
    },
    {
      key: "staging",
      title: "Staging script",
      value: savedBusinessScriptVariants?.staging || "",
    },
    {
      key: "legacy",
      title: "Legacy script",
      value: savedBusinessScriptVariants?.legacy || "",
    },
  ];

  const handleThemeLogoChange = (event) => {
    const file = event.currentTarget.files && event.currentTarget.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const { setFieldValue } = formikRef.current;
      setFieldValue("themeLogo", reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleThemePrimaryColorBlur = (event) => {
    const { setFieldValue } = formikRef.current;
    // Commit the chosen color once the user finishes interacting with the picker
    setFieldValue("themePrimaryColor", event.target.value, false);
  };

  return (
    <>
    {(loading || fetchingBusiness || (isLimitedBusinessEditor && !business)) && <Loader/>}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 2,
        }}
      >
        <Container maxWidth="xl">
          <Stack spacing={3}>
            <Typography
              variant="h4"
            >
              {title} Business
            </Typography>
            {(!isLimitedBusinessEditor || business) && (
            <Formik
              validate={validateBusinessForm}
              initialValues={initialFormValues}
              enableReinitialize
              validateOnBlur={false}
              validateOnChange={false}
              onSubmit={async (values, { setFieldError }) => {
                const editorGeneration = editorGenerationRef.current;
                const isCurrentEditor = () => editorGenerationRef.current === editorGeneration;
                const latestSystemPrompt = getLargeTextDraftValue(
                  "systemPrompt",
                  systemPromptInputRef,
                  values.systemPrompt
                );
                const latestFunctionsJson = getLargeTextDraftValue(
                  "functionsJson",
                  functionsJsonInputRef,
                  values.functionsJson
                );
                const latestRivetYaml = getLargeTextDraftValue(
                  "rivetYaml",
                  rivetYamlInputRef,
                  values.rivetYaml
                );

                const greetingButtonsSource =
                  greetingButtonEditorRef.current &&
                  typeof greetingButtonEditorRef.current.getLatestButtons === "function"
                    ? greetingButtonEditorRef.current.getLatestButtons()
                    : values.greeting_buttons;
                const linkButtonsSource =
                  linkButtonEditorRef.current &&
                  typeof linkButtonEditorRef.current.getLatestButtons === "function"
                    ? linkButtonEditorRef.current.getLatestButtons()
                    : values.link_buttons;

                const greetingButtonsInvalid =
                  hasInvalidGreetingButtons({
                    ...values,
                    greeting_buttons: greetingButtonsSource,
                  });
                setFieldError(
                  "greeting_buttons",
                  greetingButtonsInvalid
                    ? "Each greeting button must have both title and URL"
                    : undefined
                );
                if (greetingButtonsInvalid) {
                  return;
                }

                const shouldValidateLinkButtons = !isLimitedBusinessEditor;
                const linkButtonsInvalid =
                  shouldValidateLinkButtons &&
                  hasInvalidLinkButtons({
                    ...values,
                    link_buttons: linkButtonsSource,
                  });
                setFieldError(
                  "link_buttons",
                  linkButtonsInvalid
                    ? "Each button link must have both title and URL"
                    : undefined
                );
                if (linkButtonsInvalid) {
                  return;
                }

                const standardAiFunctions = Array.isArray(values.standardAiFunctions)
                  ? values.standardAiFunctions.filter(
                      (item) => typeof item === "string" && item !== "show_url_popup"
                    )
                  : [];
                const standard_ai_functions =
                  serializeStandardAiFunctionsForCreate(standardAiFunctions);
                const normalizedGreetingButtons = normalizeButtonsForPayload(
                  greetingButtonsSource,
                  {
                    includeCommentForAi: false,
                    createFallbackButton: normalizeGreetingButton,
                  }
                );
                const greeting_buttons = JSON.stringify(normalizedGreetingButtons);
                const normalizedLinkButtons = normalizeButtonsForPayload(linkButtonsSource, {
                  includeCommentForAi: true,
                  createFallbackButton: normalizeLinkButton,
                });
                const link_buttons = JSON.stringify(normalizedLinkButtons);
                const submittedBusinessFingerprint = getBusinessFormFingerprint({
                  ...values,
                  systemPrompt: latestSystemPrompt,
                  functionsJson: latestFunctionsJson,
                  rivetYaml: latestRivetYaml,
                });
                const businessHasChanges =
                  businessFormBaselineRef.current !== submittedBusinessFingerprint ||
                  hasUnsavedLargeTextEdits;
                const phorestHasChanges = hasPendingPhorestChanges(values);
                const messengerHasChanges = hasPendingMessengerChanges(values);
                const canLimitedEditorEditInstagramToken =
                  isLimitedBusinessEditor && isTruthyFlag(values.instagramDM);
                const canLimitedEditorEditWhatsAppToken =
                  isLimitedBusinessEditor && isTruthyFlag(values.whatsAppDm);

                if (isLimitedBusinessEditor && edit && fetchedBusiness) {
                  if (!Number.isSafeInteger(businessRevisionRef.current)) {
                    toast.error("Reload the business before saving again.");
                    return;
                  }
                  const limitedBusinessUpdate = {
                    id: fetchedBusiness.id,
                    expectedRevision: businessRevisionRef.current,
                    greetingMessage: values.greetingMessage,
                    greeting_buttons_enabled: values.greeting_buttons_enabled ? 1 : 0,
                    greeting_buttons,
                  };

                  if (canLimitedEditorEditInstagramToken) {
                    limitedBusinessUpdate.instagramAccessToken =
                      values.instagramAccessToken || "";
                  }

                  if (canLimitedEditorEditWhatsAppToken) {
                    limitedBusinessUpdate.whatsAppAccessToken =
                      values.whatsAppAccessToken || "";
                  }

                  const action = await updateBusinessAction(limitedBusinessUpdate);
                  if (!isCurrentEditor()) return;
                  if (action.meta?.requestStatus === "fulfilled") {
                    businessRevisionRef.current = action.payload?.revision;
                    if (!Number.isSafeInteger(businessRevisionRef.current)) {
                      setBusinessSaveNeedsReload(true);
                      toast.error("Business saved, but its revision was not confirmed. Reload before saving again.");
                      return;
                    }
                    await loadBusinessDetails(fetchedBusiness.id, {
                      fallbackMessage:
                        "Business saved, but failed to refresh business details",
                    });
                  } else {
                    businessRevisionRef.current = null;
                    setBusinessSaveNeedsReload(true);
                  }
                  return;
                }

                if (edit && fetchedBusiness) {
                  let businessSaved = false;

                  if (businessHasChanges) {
                    if (!Number.isSafeInteger(businessRevisionRef.current)) {
                      toast.error("Reload the business before saving again.");
                      return;
                    }
                    const businessUpdatePayload = {
                      id: fetchedBusiness.id,
                      expectedRevision: businessRevisionRef.current,
                      idClient: values.idClient,
                      name: values.name,
                      address: values.address,
                      websiteUrl: values.websiteUrl,
                      phone: values.phone,
                      status: "Active",
                      apiType: values.apiType,
                      hotelEmail: values.hotelEmail,
                      assistantId:values.assistantId,
                      greetingMessage: values.greetingMessage,
                      accessToken: values.accessToken,
                      clientToken: values.clientToken,
                      mewsClientId: values.mewsClientId,
                      avvioUsername: values.avvioUsername,
                      avvioPassword: values.avvioPassword,
                      hotelRedirectUrl : values.hotelRedirectUrl,
                      open_by_default: values?.open_by_default ? 1 : 0,
                      openButtonStyle: values.openButtonStyle === "new" ? "new" : null,
                      instagramDM: values?.instagramDM === true ? "yes" : "no",
                      instagramAccessToken: values?.instagramDM === true ? values?.instagramAccessToken : '',
                      whatsAppDm: values?.whatsAppDm === true ? "yes" : "no",
                      whatsAppAccessToken: values?.whatsAppAccessToken,
                      rivet: values.rivet ? 1 : 0,
                      greeting_buttons_enabled: values.greeting_buttons_enabled ? 1 : 0,
                      greeting_buttons,
                      themeOn: values.themeOn ? 1 : 0,
                      themePrimaryColor: values.themePrimaryColor || null,
                      themeFont: values.themeFont || null,
                      themeOpenButtonBottomCss: normalizeThemeOpenButtonBottomCss(
                        values.themeOpenButtonBottomCss
                      ),
                      link_buttons_enabled: values.link_buttons_enabled ? 1 : 0,
                      link_buttons,
                    };
                    addChangedLargeBusinessFields(
                      businessUpdatePayload,
                      {
                        systemPrompt: latestSystemPrompt,
                        functionsJson: latestFunctionsJson,
                        rivetYaml: latestRivetYaml,
                        themeLogo: values.themeLogo,
                      },
                      fetchedBusiness
                    );
                    addChangedStandardAiFunctionsField(
                      businessUpdatePayload,
                      standardAiFunctions,
                      fetchedBusiness
                    );

                    const action = await updateBusinessAction(businessUpdatePayload);
                    if (!isCurrentEditor()) return;
                    if (action.meta?.requestStatus !== "fulfilled") {
                      businessRevisionRef.current = null;
                      setBusinessSaveNeedsReload(true);
                      return;
                    }

                    businessRevisionRef.current = action.payload?.revision;
                    if (!Number.isSafeInteger(businessRevisionRef.current)) {
                      setBusinessSaveNeedsReload(true);
                      toast.error("Business saved, but its revision was not confirmed. Reload before saving again.");
                      return;
                    }

                    businessSaved = true;
                    businessFormBaselineRef.current = submittedBusinessFingerprint;
                    clearLargeTextDraftEdits();
                  }

                  let phorestSaved = null;
                  let messengerSaved = null;

                  if (phorestHasChanges) {
                    try {
                      const response = await updatePhorestIntegration(
                        fetchedBusiness.id,
                        buildPhorestIntegrationPayload(values)
                      );
                      if (!isCurrentEditor()) return;
                      phorestSaved = unwrapDashboardResponse(response, "Failed to save Phorest settings");
                      setPhorestIntegration(phorestSaved);
                      toast.success("Phorest integration updated");
                    } catch (error) {
                      if (!isCurrentEditor()) return;
                      const fallbackMessage = businessSaved
                        ? "Business saved, but Phorest settings were not saved"
                        : "Phorest settings were not saved";
                      toast.error(getDashboardErrorMessage(error, fallbackMessage));
                      if (businessSaved) {
                        pendingPhorestDraftRef.current = getPhorestDraftValues(values);
                      }
                    }
                  }

                  if (messengerHasChanges) {
                    try {
                      const response = await updateMessengerIntegration(
                        fetchedBusiness.id,
                        buildMessengerIntegrationPayload(values)
                      );
                      if (!isCurrentEditor()) return;
                      messengerSaved = unwrapDashboardResponse(
                        response,
                        "Failed to save Facebook Messenger settings"
                      );
                      setMessengerIntegration(messengerSaved);
                      toast.success("Facebook Messenger integration updated");
                    } catch (error) {
                      if (!isCurrentEditor()) return;
                      const fallbackMessage = businessSaved
                        ? "Business saved, but Facebook Messenger settings were not saved"
                        : "Facebook Messenger settings were not saved";
                      toast.error(getDashboardErrorMessage(error, fallbackMessage));
                      if (businessSaved) {
                        pendingMessengerDraftRef.current = getMessengerDraftValues(values);
                      }
                    }
                  }

                  if (businessSaved) {
                    const refreshedBusiness = await loadBusinessDetails(fetchedBusiness.id, {
                      fallbackMessage:
                        "Business saved, but failed to refresh business details",
                    });
                    if (!isCurrentEditor()) return;
                    if (!refreshedBusiness) {
                      pendingPhorestDraftRef.current = null;
                      pendingMessengerDraftRef.current = null;
                    }
                  }

                  if (phorestSaved) {
                    await loadPhorestIntegrationDetails(fetchedBusiness.id, phorestSaved);
                    if (!isCurrentEditor()) return;
                  }

                  if (messengerSaved) {
                    await loadMessengerIntegrationDetails(fetchedBusiness.id, messengerSaved);
                  }
                } else {
                  const action = await addBusinessAction({
                    idClient:
                      isClient && hasClientScope ? scopedClientId : values.idClient,
                    name: values.name,
                    address: values.address,
                    websiteUrl: values.websiteUrl,
                    phone: values.phone,
                    apiType: values.apiType,
                    hotelEmail: values.hotelEmail,
                    assistantId: values.assistantId,
                    greetingMessage: values.greetingMessage,
                    accessToken: values.apiType === "mews" ? values.accessToken : "",
                    clientToken: values.apiType === "mews" ? values.clientToken : "",
                    mewsClientId: values.apiType === "mews" ? values.mewsClientId : "",
                    avvioUsername: values.apiType === "avvio" ? values.avvioUsername : "",
                    avvioPassword: values.apiType === "avvio" ? values.avvioPassword : "",
                    hotelRedirectUrl : values.apiType === "noReservation" ? values.hotelRedirectUrl : "",
                    open_by_default: values?.open_by_default ? 1 : 0,
                    openButtonStyle: values.openButtonStyle === "new" ? "new" : null,
                    instagramDM: values?.instagramDM === true ? "yes" : "no",
                    instagramAccessToken: values?.instagramAccessToken,
                    whatsAppDm: values?.whatsAppDm === true ? "yes" : "no",
                    whatsAppAccessToken: values?.whatsAppAccessToken,
                    rivet: values.rivet ? 1 : 0,
                    system_prompt: nullIfEmptyBusinessField(latestSystemPrompt),
                    functions_json: nullIfEmptyBusinessField(latestFunctionsJson),
                    rivet_yaml: nullIfEmptyBusinessField(latestRivetYaml),
                    standard_ai_functions,
                    greeting_buttons_enabled: values.greeting_buttons_enabled ? 1 : 0,
                    greeting_buttons,
                    themeOn: values.themeOn ? 1 : 0,
                    themeLogo: nullIfEmptyBusinessField(values.themeLogo),
                    themePrimaryColor: values.themePrimaryColor || null,
                    themeFont: values.themeFont || null,
                    themeOpenButtonBottomCss: normalizeThemeOpenButtonBottomCss(
                      values.themeOpenButtonBottomCss
                    ),
                    link_buttons_enabled: values.link_buttons_enabled ? 1 : 0,
                    link_buttons,
                  });
                  if (!isCurrentEditor()) return;
                  if (action.meta?.requestStatus === "fulfilled") {
                    navigate("/business");
                  }
                }
              }}
              innerRef={formikRef}
            >
              {(formik) => {
                const {
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  setFieldError,
                  setFieldValue,
                  values,
                  isSubmitting,
                  submitCount,
                } = formik;
                const hasUnsavedWidgetConfigChanges =
                  getBusinessFormFingerprint(values) !== businessFormBaselineRef.current ||
                  hasUnsavedLargeTextEdits;
                const showValidationErrors = submitCount > 0;
                const apiValidationFields = {
                  mews: ["mewsClientId", "clientToken", "accessToken"],
                  avvio: ["avvioUsername", "avvioPassword"],
                  noReservation: ["hotelRedirectUrl"],
                };
                const hasApiValidationError =
                  showValidationErrors &&
                  (apiValidationFields[values.apiType] || []).some(
                    (fieldName) => Boolean(errors[fieldName])
                  );
                const canLimitedEditorEditInstagramToken =
                  isLimitedBusinessEditor && isTruthyFlag(values.instagramDM);
                const canLimitedEditorEditWhatsAppToken =
                  isLimitedBusinessEditor && isTruthyFlag(values.whatsAppDm);
                return (
                  <>
                    <form onSubmit={handleSubmit} className={classes.card_form} spellCheck={false} autoComplete="off">
                      <Card className={classes.Card}>
                      <CardContent className={classes.form_body}>
                        {isLimitedBusinessEditor ? (
                          <Grid
                            container
                            columnSpacing={6}
                            rowSpacing={3}
                            alignItems="flex-start"
                            justifyContent={"start"}
                          >
                            <Grid item xs={12} md={8} lg={7}>
                              <Stack spacing={3}>
                                <Box sx={{ mt: 3 }}>
                                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                    {fetchedBusiness?.name || values.name || "Business settings"}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Client and business users can edit only the fields below.
                                    All other business settings remain admin-controlled.
                                  </Typography>
                                </Box>

                                <SocialTokenFields
                                  values={values}
                                  handleChange={handleChange}
                                  handleBlur={handleBlur}
                                  errors={errors}
                                  touched={touched}
                                  isLimitedMode
                                  showInstagramToken={canLimitedEditorEditInstagramToken}
                                  showWhatsAppToken={canLimitedEditorEditWhatsAppToken}
                                />

                                <Card variant="outlined" sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                                  <CardContent sx={{ pt: 2.5 }}>
                                    <Stack spacing={2}>
                                      <TextField
                                        label="Greeting Message"
                                        name="greetingMessage"
                                        type="text"
                                        minRows={4}
                                        multiline
                                        className={clsx(
                                          classes.textfield,
                                          errors.greetingMessage && touched.greetingMessage
                                            ? classes.inputError
                                            : classes.inputNoError
                                        )}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.greetingMessage}
                                      />
                                      <ErrorMessage
                                        name="greetingMessage"
                                        component="span"
                                        className={classes.error}
                                      />
                                    </Stack>
                                  </CardContent>
                                </Card>
                              </Stack>
                            </Grid>

                            <Grid item xs={12}>
                              <LinkButtonEditor
                                ref={greetingButtonEditorRef}
                                values={values}
                                setFieldValue={setFieldValue}
                                setFieldError={setFieldError}
                                createEmptyButton={normalizeGreetingButton}
                                validateButtons={hasInvalidGreetingButtons}
                                error={errors.greeting_buttons}
                                buttonsField="greeting_buttons"
                                enabledField="greeting_buttons_enabled"
                                sectionTitle="Greeting buttons"
                                toggleAriaLabel="Enable greeting buttons"
                                addButtonLabel="Add greeting button"
                                showCommentField={false}
                                keyPrefix="greeting"
                                validationErrorMessage="Each greeting button must have both title and URL"
                              />
                            </Grid>
                          </Grid>
                        ) : (
                        <Grid
                          container
                          columnSpacing={6}
                          rowSpacing={3}
                          alignItems="flex-start"
                          justifyContent={"start"}
                        >
                          <Grid item xs={12} md={5}>
                            <Stack spacing={4}>
                              <Box sx={{ mt: 3 }}>
                                <Typography
                                  variant="h6"
                                  sx={{ fontWeight: 700, mb: 1 }}
                                >
                                  General
                                </Typography>
                                <Grid container spacing={0.875}>
                                  <Grid item xs={12}>
                                    <Stack>
                                      <TextField
                                        label="Name"
                                        name="name"
                                        className={clsx(
                                          classes.textfield,
                                          errors.name && touched.name
                                            ? classes.inputError
                                            : classes.inputNoError
                                        )}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.name}
                                      />
                                          <ErrorMessage
                                        name="name"
                                        component="span"
                                        className={classes.error}
                                      />
                                    </Stack>
                                  </Grid>
                                  {capabilities?.canManageClients ? (
                                    <Grid item xs={12}>
                                      <Stack>
                                        <Autocomplete
                                          id="autocompelete-idclient"
                                          className={clsx(
                                            classes.textfield,
                                            errors.idClient && touched.idClient
                                              ? classes.inputError
                                              : classes.inputNoError
                                          )}
                                          name="idClient"
                                          options={clientId || []}
                                          getOptionLabel={(option) => option.name}
                                          value={
                                            values.idClient ? clientById[values.idClient] || null : null
                                          }
                                          onChange={(event, newValue) => {
                                            const { setFieldValue } = formikRef.current;
                                            if (newValue) {
                                              setFieldValue("idClient", newValue.id);
                                            } else {
                                              setFieldValue("idClient", null);
                                            }
                                          }}
                                          onBlur={handleBlur}
                                          sx={{ width: "100%" }}
                                          renderInput={(params) => (
                                            <TextField
                                              {...params}
                                              label="Select Client"
                                            />
                                          )}
                                        />
                                        <ErrorMessage
                                          name="idClient"
                                          component="span"
                                          className={classes.error}
                                        />
                                      </Stack>
                                    </Grid>
                                  ) : null}
                                </Grid>
                              </Box>

                              <Box sx={{ mt: 3 }}>
                                <Typography
                                  variant="h6"
                                  sx={{ fontWeight: 700, mb: 1 }}
                                >
                                  Contacts
                                </Typography>
                                <Grid container spacing={0.875}>
                                  <Grid item xs={12}>
                                    <Stack>
                                      <TextField
                                        label="WebsiteUrl"
                                        name="websiteUrl"
                                        className={clsx(
                                          classes.textfield,
                                          errors.websiteUrl && touched.websiteUrl
                                            ? classes.inputError
                                            : classes.inputNoError
                                        )}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.websiteUrl}
                                      />
                                      <ErrorMessage
                                        name="websiteUrl"
                                        component="span"
                                        className={classes.error}
                                      />
                                    </Stack>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Stack>
                                      <TextField
                                        label="Phone Number"
                                        name="phone"
                                        className={clsx(
                                          classes.textfield,
                                          errors.phone && touched.phone
                                            ? classes.inputError
                                            : classes.inputNoError
                                        )}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.phone}
                                      />
                                      <ErrorMessage
                                        name="phone"
                                        component="span"
                                        className={classes.error}
                                      />
                                    </Stack>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Stack>
                                      <TextField
                                        label="Hotel Email"
                                        name="hotelEmail"
                                        className={clsx(
                                          classes.textfield,
                                          errors.hotelEmail && touched.hotelEmail
                                            ? classes.inputError
                                            : classes.inputNoError
                                        )}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.hotelEmail}
                                      />
                                      <ErrorMessage
                                        name="hotelEmail"
                                        component="span"
                                        className={classes.error}
                                      />
                                    </Stack>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Stack>
                                      <TextField
                                        label="Address"
                                        name="address"
                                        type="text"
                                        minRows={4}
                                        multiline
                                        className={clsx(
                                          classes.textfield,
                                          errors.address && touched.address
                                            ? classes.inputError
                                            : classes.inputNoError
                                        )}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.address}
                                      />
                                      <ErrorMessage
                                        name="address"
                                        component="span"
                                        className={classes.error}
                                      />
                                    </Stack>
                                  </Grid>
                                </Grid>
                              </Box>
                            </Stack>
                          </Grid>

                          <Grid item xs={12} md={7}>
                            <Stack spacing={1.5}>
                              <APIConfigSection
                                values={values}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                errors={errors}
                                touched={touched}
                                hasValidationError={hasApiValidationError}
                                sectionStateKey={formSeed}
                              />

                              <SocialTokenFields
                                values={values}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                errors={errors}
                                touched={touched}
                                showPhorestSection={
                                  edit &&
                                  Boolean(business?.id) &&
                                  capabilities?.canCreateBusinesses === true
                                }
                                showMessengerSection={
                                  edit &&
                                  Boolean(business?.id) &&
                                  capabilities?.canCreateBusinesses === true
                                }
                                phorestLoading={phorestLoading}
                                phorestLoadError={phorestLoadError}
                                messengerLoading={messengerLoading}
                                messengerLoadError={messengerLoadError}
                                showValidationErrors={showValidationErrors}
                                integrationStateKey={formSeed}
                              />

                              <LegacyAssistantSection
                                values={values}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                errors={errors}
                                touched={touched}
                                classes={classes}
                                hasValidationError={
                                  showValidationErrors && Boolean(errors.assistantId)
                                }
                                sectionStateKey={formSeed}
                              />
                            </Stack>
                          </Grid>
                          <Grid item xs={12}>
                            <Divider sx={{ my: 4 }} />

                            <Box sx={{ mt: 3 }}>
                              <Typography
                                variant="h5"
                                sx={{ fontWeight: 700, mb: 2 }}
                              >
                                Chat widget appearance
                              </Typography>
                              <Grid container rowSpacing={3} alignItems="flex-start">
                                <Grid item xs={12}>
                                  <Stack spacing={1}>
                                    <TextField
                                      label="Greeting Message"
                                      name="greetingMessage"
                                      type="text"
                                      minRows={4}
                                      multiline
                                      className={clsx(
                                        classes.textfield,
                                        errors.greetingMessage && touched.greetingMessage
                                          ? classes.inputError
                                          : classes.inputNoError
                                      )}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values.greetingMessage}
                                    />
                                    <ErrorMessage
                                      name="greetingMessage"
                                      component="span"
                                      className={classes.error}
                                    />
                                  </Stack>
                                </Grid>

                                <Grid item xs={12}>
                                  <LinkButtonEditor
                                    ref={greetingButtonEditorRef}
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    setFieldError={setFieldError}
                                    createEmptyButton={normalizeGreetingButton}
                                    validateButtons={hasInvalidGreetingButtons}
                                    error={errors.greeting_buttons}
                                    buttonsField="greeting_buttons"
                                    enabledField="greeting_buttons_enabled"
                                    sectionTitle="Greeting buttons"
                                    toggleAriaLabel="Enable greeting buttons"
                                    addButtonLabel="Add greeting button"
                                    showCommentField={false}
                                    keyPrefix="greeting"
                                    validationErrorMessage="Each greeting button must have both title and URL"
                                  />
                                </Grid>

                                <Grid item xs={12}>
                                  <Stack spacing={2}>
                                    <Box>
                                      <Typography
                                        variant="h6"
                                        sx={{ fontWeight: 700, mb: 0 }}
                                      >
                                        Custom theme
                                      </Typography>
                                      <FormGroup
                                        row
                                        className={classes.toggle_container}
                                        sx={{ mt: 1 }}
                                      >
                                        <FormControlLabel
                                          control={
                                            <Switch
                                              checked={!!values.themeOn}
                                              onChange={(event) => {
                                                const { setFieldValue } = formikRef.current;
                                                setFieldValue("themeOn", event.target.checked);
                                              }}
                                              color="primary"
                                            />
                                          }
                                          label="Enable custom theme"
                                        />
                                      </FormGroup>
                                    </Box>

                                    {values.themeOn ? (
                                      <Card
                                        variant="outlined"
                                        sx={{ backgroundColor: '#f5f5f5', borderRadius: 1 }}
                                      >
                                        <CardContent sx={{ pt: 2.5, pb: 0 }}>
                                          <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                              <Stack spacing={1}>
                                                <FormLabel>Widget logo</FormLabel>
                                                <Stack direction="row" spacing={2} alignItems="flex-start">
                                                  {values.themeLogo && (
                                                    <Box
                                                      sx={{
                                                        width: 100,
                                                        height: 100,
                                                        borderRadius: '50%',
                                                        border: '1px solid #e0e0e0',
                                                        backgroundColor: '#ffffff',
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        overflow: 'hidden',
                                                      }}
                                                    >
                                                      <Box
                                                        component="img"
                                                        src={values.themeLogo}
                                                        alt="Widget logo preview"
                                                        sx={{
                                                          maxWidth: '100%',
                                                          maxHeight: '100%',
                                                          objectFit: 'contain',
                                                        }}
                                                      />
                                                    </Box>
                                                  )}
                                                  <Stack spacing={1}>
                                                    <Button
                                                      variant="outlined"
                                                      component="label"
                                                    >
                                                      Upload image
                                                      <input
                                                        type="file"
                                                        accept="image/*"
                                                        hidden
                                                        onChange={handleThemeLogoChange}
                                                      />
                                                    </Button>
                                                    {values.themeLogo && (
                                                      <Button
                                                        variant="contained"
                                                        color="error"
                                                        onClick={() => {
                                                          const { setFieldValue } = formikRef.current;
                                                          setFieldValue('themeLogo', '');
                                                        }}
                                                      >
                                                        Remove image
                                                      </Button>
                                                    )}
                                                  </Stack>
                                                </Stack>
                                              </Stack>
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                              <Stack spacing={1}>
                                                <FormLabel>Primary color</FormLabel>
                                                <TextField
                                                  type="color"
                                                  defaultValue={values.themePrimaryColor || initValues.themePrimaryColor}
                                                  onBlur={handleThemePrimaryColorBlur}
                                                  sx={{
                                                    width: 100,
                                                    '& input[type="color"]': {
                                                      padding: 0,
                                                      width: 100,
                                                      height: 100,
                                                    },
                                                  }}
                                                />
                                              </Stack>
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                              <Stack spacing={1}>
                                                <FormLabel>Font</FormLabel>
                                                <TextField
                                                  label="Font name"
                                                  name="themeFont"
                                                  value={values.themeFont}
                                                  onChange={handleChange}
                                                  onBlur={handleBlur}
                                                  placeholder="Inter"
                                                  className={classes.textfield}
                                                  InputProps={whiteInputProps}
                                                  sx={whiteFieldSx}
                                                />
                                              </Stack>
                                            </Grid>
                                          </Grid>
                                        </CardContent>
                                      </Card>
                                    ) : null}
                                  </Stack>
                                </Grid>

                                <Grid item xs={12}>
                                  <Stack
                                    spacing={2}
                                    sx={{ maxWidth: 320, alignItems: 'flex-start' }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Typography
                                        variant="h6"
                                        sx={{ fontWeight: 700, mb: 0 }}
                                      >
                                        Open by default
                                      </Typography>
                                      <FormGroup
                                        row
                                        className={classes.toggle_container}
                                        sx={{ ml: 2 }}
                                      >
                                        <Switch
                                          checked={values.open_by_default}
                                          onChange={handleChange}
                                          name="open_by_default"
                                          color="primary"
                                        />
                                      </FormGroup>
                                    </Box>
                                    <Autocomplete
                                      id="open-button-style"
                                      name="openButtonStyle"
                                      options={["none", "new"]}
                                      value={values.openButtonStyle || "none"}
                                      onChange={(event, newValue) => {
                                        const { setFieldValue } = formikRef.current;
                                        setFieldValue("openButtonStyle", newValue || "none");
                                      }}
                                      sx={{ width: '100%' }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label="Open button style"
                                        />
                                      )}
                                    />
                                    <TextField
                                      fullWidth
                                      label="Open button elevation"
                                      name="themeOpenButtonBottomCss"
                                      type="text"
                                      className={clsx(
                                        classes.textfield,
                                        errors.themeOpenButtonBottomCss && touched.themeOpenButtonBottomCss
                                          ? classes.inputError
                                          : classes.inputNoError
                                      )}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values.themeOpenButtonBottomCss}
                                      helperText="Example: '15vh' = 15% of window hight"
                                    />
                                  </Stack>
                                </Grid>
                              </Grid>
                            </Box>
                          </Grid>

                        <Grid item xs={12}>
                          <Divider sx={{ my: 4 }} />

                          <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, mb: 1 }}
                          >
                            Assistant Type
                          </Typography>
                          <FormControl>
                            <RadioGroup
                              onChange={(e) => {
                                const { setFieldValue } = formikRef.current;
                                setFieldValue("rivet", e.target.value === "rivet");
                              }}
                              value={values.rivet ? "rivet" : "simpleAI"}
                              name="assistantType"
                              row
                              aria-labelledby="assistant-type-radio-buttons-group-label"
                            >
                              <FormControlLabel value="simpleAI" control={<Radio />} label="Simple AI" />
                              <FormControlLabel value="rivet" control={<Radio />} label="Rivet Workflow" />
                            </RadioGroup>
                          </FormControl>

                          <Grid container spacing={0.875} sx={{ mt: 2 }}>
                          {!values.rivet && (
                            <>
                              <Grid item xs={12} md={6}>
                                <Stack sx={{ height: '100%' }}>
                                  <TextField
                                    key={`system-prompt-${formSeed}`}
                                    label="System Prompt"
                                    name="systemPrompt"
                                    type="text"
                                    rows={8}
                                    multiline
                                    className={clsx(
                                      classes.textfield,
                                      errors.systemPrompt && touched.systemPrompt
                                        ? classes.inputError
                                        : classes.inputNoError
                                    )}
                                    inputRef={systemPromptInputRef}
                                    defaultValue={values.systemPrompt}
                                    onChange={(event) => {
                                      setLargeTextDraftValue(
                                        "systemPrompt",
                                        event.target.value,
                                        initialFormValues.systemPrompt
                                      );
                                    }}
                                    onBlur={(event) => {
                                      systemPromptScrollTopRef.current = event.target.scrollTop;
                                      handleBlur(event);
                                      if (event.target.value !== values.systemPrompt) {
                                        setFieldValue("systemPrompt", event.target.value, false);
                                      }
                                      setLargeTextDraftValue(
                                        "systemPrompt",
                                        event.target.value,
                                        initialFormValues.systemPrompt
                                      );
                                    }}
                                    onScroll={(event) => {
                                      systemPromptScrollTopRef.current = event.target.scrollTop;
                                    }}
                                    onMouseDown={() => {
                                      if (systemPromptInputRef.current) {
                                        systemPromptScrollTopRef.current =
                                          systemPromptInputRef.current.scrollTop;
                                      }
                                    }}
                                    onFocus={() => {
                                      const savedScrollTop = systemPromptScrollTopRef.current;
                                      const inputElement = systemPromptInputRef.current;
                                      if (savedScrollTop > 0 && inputElement) {
                                        const restoreScroll = () => {
                                          inputElement.scrollTop = savedScrollTop;
                                        };
                                        requestAnimationFrame(() => {
                                          restoreScroll();
                                        });
                                        setTimeout(restoreScroll, 0);
                                        setTimeout(restoreScroll, 50);
                                      }
                                    }}
                                    InputProps={{ 
                                      sx: { 
                                        height: '100%',
                                        alignItems: 'flex-start',
                                        '& .MuiInputBase-inputMultiline': { 
                                          maxHeight: 200, 
                                          overflow: 'auto !important' 
                                        } 
                                      } 
                                    }}
                                  />
                                  <ErrorMessage
                                    name="systemPrompt"
                                    component="span"
                                    className={classes.error}
                                  />
                                </Stack>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Stack sx={{ height: '100%' }}>
                                  <TextField
                                    key={`functions-json-${formSeed}`}
                                    label="Custom AI functions"
                                    name="functionsJson"
                                    type="text"
                                    rows={8}
                                    multiline
                                    className={clsx(
                                      classes.textfield,
                                      errors.functionsJson && touched.functionsJson
                                        ? classes.inputError
                                        : classes.inputNoError
                                    )}
                                    inputRef={functionsJsonInputRef}
                                    defaultValue={values.functionsJson}
                                    onChange={(event) => {
                                      setLargeTextDraftValue(
                                        "functionsJson",
                                        event.target.value,
                                        initialFormValues.functionsJson
                                      );
                                    }}
                                    onBlur={(event) => {
                                      handleBlur(event);
                                      if (event.target.value !== values.functionsJson) {
                                        setFieldValue("functionsJson", event.target.value, false);
                                      }
                                      setLargeTextDraftValue(
                                        "functionsJson",
                                        event.target.value,
                                        initialFormValues.functionsJson
                                      );
                                    }}
                                    InputProps={{ 
                                      sx: { 
                                        height: '100%',
                                        alignItems: 'flex-start',
                                        '& .MuiInputBase-inputMultiline': { 
                                          maxHeight: 200, 
                                          overflow: 'auto !important' 
                                        } 
                                      } 
                                    }}
                                  />
                                  <ErrorMessage
                                    name="functionsJson"
                                    component="span"
                                    className={classes.error}
                                  />
                                </Stack>
                              </Grid>
                              <Grid item xs={12}>
                                <Stack sx={{ mt: 2 }}>
                                  <Typography
                                    variant="h6"
                                          sx={{ fontWeight: 700, mb: 0 }}
                                  >
                                    Standard AI functions
                                  </Typography>
                                  <FormGroup row>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={
                                            Array.isArray(values.standardAiFunctions) &&
                                            values.standardAiFunctions.includes("get_weather")
                                          }
                                          onChange={(event) => {
                                            const { setFieldValue } = formikRef.current;
                                            const current = Array.isArray(values.standardAiFunctions)
                                              ? values.standardAiFunctions
                                              : [];
                                            let next;
                                            if (event.target.checked) {
                                              next = current.includes("get_weather")
                                                ? current
                                                : [...current, "get_weather"];
                                            } else {
                                              next = current.filter((item) => item !== "get_weather");
                                            }
                                            setFieldValue("standardAiFunctions", next);
                                          }}
                                        />
                                      }
                                      label="get_weather"
                                    />
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={
                                            Array.isArray(values.standardAiFunctions) &&
                                            values.standardAiFunctions.includes("get_google_results")
                                          }
                                          onChange={(event) => {
                                            const { setFieldValue } = formikRef.current;
                                            const current = Array.isArray(values.standardAiFunctions)
                                              ? values.standardAiFunctions
                                              : [];
                                            let next;
                                            if (event.target.checked) {
                                              next = current.includes("get_google_results")
                                                ? current
                                                : [...current, "get_google_results"];
                                            } else {
                                              next = current.filter((item) => item !== "get_google_results");
                                            }
                                            setFieldValue("standardAiFunctions", next);
                                          }}
                                        />
                                      }
                                      label="get_google_results"
                                    />
                                  </FormGroup>
                                </Stack>
                              </Grid>

                              <Grid item xs={12} sx={{ mt: 2 }}>
                                <LinkButtonEditor
                                  ref={linkButtonEditorRef}
                                  values={values}
                                  setFieldValue={setFieldValue}
                                  setFieldError={setFieldError}
                                  createEmptyButton={normalizeLinkButton}
                                  validateButtons={hasInvalidLinkButtons}
                                  error={errors.link_buttons}
                                />
                              </Grid>
                            </>
                          )}

                          {values.rivet && (
                            <Grid item xs={12}>
                              <Stack>
                                <TextField
                                  key={`rivet-yaml-${formSeed}`}
                                  label="Rivet YAML"
                                  name="rivetYaml"
                                  type="text"
                                  rows={8}
                                  multiline
                                  className={clsx(
                                    classes.textfield,
                                    errors.rivetYaml && touched.rivetYaml
                                      ? classes.inputError
                                      : classes.inputNoError
                                  )}
                                  inputRef={rivetYamlInputRef}
                                  defaultValue={values.rivetYaml}
                                  onChange={(event) => {
                                    setLargeTextDraftValue(
                                      "rivetYaml",
                                      event.target.value,
                                      initialFormValues.rivetYaml
                                    );
                                  }}
                                  onBlur={(event) => {
                                    handleBlur(event);
                                    if (event.target.value !== values.rivetYaml) {
                                      setFieldValue("rivetYaml", event.target.value, false);
                                    }
                                    setLargeTextDraftValue(
                                      "rivetYaml",
                                      event.target.value,
                                      initialFormValues.rivetYaml
                                    );
                                  }}
                                  InputProps={{ 
                                    sx: { 
                                      '& .MuiInputBase-inputMultiline': { 
                                        maxHeight: 300, 
                                        overflow: 'auto !important' 
                                      } 
                                    } 
                                  }}
                                />
                                <ErrorMessage
                                  name="rivetYaml"
                                  component="span"
                                  className={classes.error}
                                />
                              </Stack>
                            </Grid>
                          )}
                          </Grid>
                        </Grid>
                        </Grid>
                        )}
                      </CardContent>
                      </Card>

                      {businessSaveNeedsReload && (
                        <Box role="alert" sx={{ mt: 3 }}>
                          <Typography>Reload the business before saving again. Reloading may discard unsaved changes.</Typography>
                          <Button type="button" disabled={isSubmitting} onClick={() => loadBusinessDetails(fetchedBusiness.id)}>
                            Reload business
                          </Button>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 5, mb: 5 }}>
                        <Button
                          type="reset"
                          className={classes.button}
                          variant="contained"
                          onClick={() => navigate("/business")}
                          color="error"
                        >
                          Cancel all changes and close
                        </Button>
                        <Button
                          className={classes.button}
                          variant="contained"
                          sx={{
                            backgroundColor: colorSet.color,
                            ":hover": { backgroundColor: colorSet.color },
                            color: 'white',
                          }}
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {edit ? "Save changes" : "Add Business"}
                        </Button>
                      </Box>
                    </form>
                    {!isLimitedBusinessEditor && (
                      <Box className={classes.second_card}>
                        <WidgetScriptSection
                          scriptSections={scriptSections}
                          copiedTarget={copiedTarget}
                          onCopy={copyToClipboard}
                          isDirty={hasUnsavedWidgetConfigChanges}
                          cardClassName={classes.Card}
                        />
                      </Box>
                    )}
                  </>
                );
              }}
            </Formik>
            )}
          </Stack>
        </Container>
      </Box>
    </>
  );
}
export default Index;
